/**
 * 概览数据 Hook
 */
import { useState, useCallback, useEffect } from 'react';
import {
  getWeeklyOverview,
  IOverviewData,
  IOverviewDataRaw,
  IKeyMetricsTrendItem,
} from '@op/services/weeklyReport';
import { useCancelRequestHook } from '@op/hooks/useCancelRequest';
import { DBO_REQ, DBO_V } from '@op/utils/format';
import { ParamsTimeType } from '@op/types/common';
import type { TRequestBackType } from '@op/utils/requestUtil';

interface IUseOverviewDataOptions {
  autoFetch?: boolean;
}

interface IUseOverviewDataResult {
  /** 概览数据（用于指标卡片） */
  data: IOverviewData | null;
  /** 图表数据（符合 TRequestBackType 格式） */
  chartData: TRequestBackType | null;
  /** 链接时间范围 */
  linkTime: { start_time: string; end_time: string } | null;
  loading: boolean;
  error: boolean;
  fetchData: (params: any) => Promise<any>;
}

/**
 * 将趋势数据转换为 TRequestBackType 格式（使用 DBO_REQ.moreKeys）
 */
const transformTrendDataToChartType = (
  trendData: IKeyMetricsTrendItem[],
  startTime: string,
  endTime: string,
  dateType: ParamsTimeType = 'daily',
): TRequestBackType => {
  if (!trendData || trendData.length === 0) {
    return { isEmpty: true };
  }

  // 过滤掉不需要展示的字段 total_mentions
  const filteredTrendData = trendData.map(({ total_mentions, ...rest }) => rest);

  // 使用 DBO_REQ.moreKeys 处理数据转换（包含表格数据）
  return DBO_REQ.moreKeys({
    timeline: filteredTrendData,
    timeline_of_start_time: startTime,
    timeline_of_end_time: endTime,
    filter: { date_type: dateType },
    timeKey: 'date',
    addCountValue: item => ({
      // 将 mentions_ratio 转换为百分比
      mentions_ratio_percent: item.mentions_ratio * 100,
      // 保留 new_add_keyword 标记，用于散点图
      new_add_keyword: item.new_add_keyword,
    }),
    needTable: true, // 返回表格数据，用于下载功能
    moreLineTable: true,
  });
};

export const useOverviewData = (
  filter: any,
  options?: IUseOverviewDataOptions,
): IUseOverviewDataResult => {
  const { autoFetch = true } = options || {};
  const [data, setData] = useState<IOverviewData | null>(null);
  const [chartData, setChartData] = useState<TRequestBackType | null>(null);
  const [linkTime, setLinkTime] = useState<{ start_time: string; end_time: string } | null>(null);
  const { loading, error, wrapperCancelRequest } =
    useCancelRequestHook<IOverviewDataRaw>({
      clear: () => {
        setData(null);
        setChartData(null);
        setLinkTime(null);
      },
    });

  const fetchData = useCallback((params: any) => {
    return wrapperCancelRequest(getWeeklyOverview, params)
      .then(res => {
        const rawData = res;
        if (!rawData) return res;

        // 处理指标数据（从 rawData.summary 提取并计算环比）
        const {
          summary,
          trend_time,
          link_time,
        } = rawData;
        // DBO_V.qoq 返回百分比，需除以 100 转为小数
        const mentionChangeRate = DBO_V.qoq(summary.mentions.new, summary.mentions.old) / 100;
        const engagementChangeRate = DBO_V.qoq(summary.engagement.new, summary.engagement.old) / 100;

        setData({
          mention: summary.mentions.new,
          mention_change_rate: mentionChangeRate,
          engagement: summary.engagement.new,
          engagement_change_rate: engagementChangeRate,
          prev_mention: summary.mentions.old,
          prev_engagement: summary.engagement.old,
          start_time: trend_time.start_time,
          end_time: trend_time.end_time,
          prev_start_time: link_time.start_time,
          prev_end_time: link_time.end_time,
        });

        // 设置 linkTime
        setLinkTime(link_time);

        // 处理图表数据（chart SKILL 规范，使用 DBO_REQ.moreKeys）
        if (rawData.trend) {
          const chartData = transformTrendDataToChartType(
            rawData.trend,
            rawData.trend_time.start_time,
            rawData.trend_time.end_time,
            params.date_type || 'daily',
          );
          setChartData(chartData);
        }

        return res;
      })
      .catch(err => {
        console.error('获取概览数据失败:', err);
      });
  }, []);

  useEffect(() => {
    if (!filter || !autoFetch) return;
    fetchData(filter);
  }, [filter, autoFetch, fetchData]);

  return { data, chartData, linkTime, loading, error, fetchData };
};

export default useOverviewData;
